import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import AuthStore from "../../stores/AuthStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Label } from "../ui/label";
import { useTableView } from "@/hooks/useTableView";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CreateTaskButton } from "../create-task-button";
import { Badge } from "../ui/badge";
import { getMyTaskCount } from "../../data/requests/task-requests";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../ui/sheet";
import {
  BarChart3,
  Building2,
  CreditCard,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  PanelsTopLeft,
  Shield,
  ShieldCheck,
  UserRound,
  Users2,
  X,
  ChevronsLeft,
} from "lucide-react";
import { ThemeToggle } from "../ui/theme-toggle";

export function NavbarApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { viewMode, setViewMode } = useTableView();
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarVisible, setDesktopSidebarVisible] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("sidebar-visible");
      if (saved !== null) {
        return saved === "true";
      }
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("sidebar-visible", isDesktopSidebarVisible);
      window.__SIDEBAR_VISIBLE = isDesktopSidebarVisible;
      window.dispatchEvent(
        new CustomEvent("sidebar-visibility-change", {
          detail: { isVisible: isDesktopSidebarVisible },
        })
      );
    }
  }, [isDesktopSidebarVisible]);

  // Bana atanan task sayısını getir
  const { data: taskCountData } = useQuery({
    queryKey: ["my-task-count"],
    queryFn: () => getMyTaskCount().then((res) => res.data),
    refetchInterval: 30000, // Her 30 saniyede bir güncelle
  });

  const myTaskCount = taskCountData?.count || 0;

  const navigationMenu = [
    {
      displayName: "Panel",
      path: "/mydashboard",
      isHavePermission: true,
      icon: LayoutDashboard,
    },
    {
      displayName: "Hızlı Erişim",
      path: "/dashboard",
      isHavePermission: true,
      icon: PanelsTopLeft,
    },
    {
      displayName: "Müşteriler",
      path: "/clients",
      isHavePermission: AuthStore.isHavePermission("CLIENT_READ"),
      icon: Users2,
    },
    {
      displayName: "Ajanslar",
      path: "/agencies",
      isHavePermission: AuthStore.isHavePermission("AGENCY_READ"),
      icon: Building2,
    },
    {
      displayName: "Ödemeler",
      path: "/payments",
      isHavePermission: AuthStore.isHavePermission("PAYMENT_READ"),
      icon: CreditCard,
    },
    {
      displayName: "Mutabakatlar",
      path: "/confirmations",
      isHavePermission: AuthStore.isHavePermission("CONFIRMATION_READ"),
      icon: ShieldCheck,
    },
    {
      displayName: "Raporlar",
      path: "/app/reports",
      isHavePermission: true,
      icon: BarChart3,
    },
    {
      displayName: "Görevler",
      path: "/app/tasks",
      isHavePermission: true,
      icon: ListChecks,
      badge: myTaskCount > 0 ? myTaskCount : null,
    },
  ];

  const secondaryMenu = [
    {
      displayName: "Profil",
      path: "/profile",
      isHavePermission: true,
      icon: UserRound,
    },
    {
      displayName: "Yönetim",
      path: "/admin",
      isHavePermission: AuthStore.isHavePermission("ADMIN") === true,
      icon: Shield,
    },
  ];

  const logout = () => {
    // localstorage  must be clear in here
    queryClient.clear();

    AuthStore.logout();
    navigate("/");
  };

  const renderMenuLink = (menu, onNavigate) => {
    const Icon = menu.icon;
    return (
      <Link
        key={menu.path}
        className={cn(
          "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground",
          location.pathname.startsWith(menu.path) &&
            "bg-white/15 text-foreground"
        )}
        to={menu.path}
        onClick={() => onNavigate?.()}
      >
        {Icon && <Icon className="h-4 w-4" />}
        <span className="flex-1 truncate">{menu.displayName}</span>
        {menu.badge && (
          <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1 text-xs">
            {menu.badge}
          </Badge>
        )}
      </Link>
    );
  };

  const SidebarContent = ({
    isMobile = false,
    onNavigate,
    onCollapseDesktop,
  }) => (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-4 py-5">
        <Link
          className="flex items-center text-lg font-semibold tracking-tight text-foreground"
          to="/dashboard"
          onClick={() => {
            if (isMobile) {
              onNavigate?.();
            }
          }}
        >
          NewTurk PORTAL
          {process.env.NODE_ENV === "development" && (
            <span className="ml-2 rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold text-red-700">
              LOCAL
            </span>
          )}
        </Link>
        {isMobile ? (
          <Button variant="ghost" size="icon" onClick={() => onNavigate?.()}>
            <X className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={() => onCollapseDesktop?.()}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        )}
      </div>
      <Separator />
      <div className="flex-1 space-y-6 overflow-y-auto px-4 py-6">
        <CreateTaskButton className="w-full" />
        <div className="rounded-xl border bg-muted/40 px-4 py-3">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Görünüm
          </Label>
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="grid">Grid Görünümü</SelectItem>
              <SelectItem value="table">Tablo Görünümü</SelectItem>
              <SelectItem value="advancedTable">Gelişmiş Tablo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <nav className="space-y-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Menüler
          </p>
          {navigationMenu
            .filter((menu) => menu.isHavePermission)
            .map((menu) =>
              renderMenuLink(menu, () => {
                if (isMobile) {
                  onNavigate?.();
                }
              })
            )}
        </nav>

        <nav className="space-y-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Hesap
          </p>
          {secondaryMenu
            .filter((menu) => menu.isHavePermission)
            .map((menu) =>
              renderMenuLink(menu, () => {
                if (isMobile) {
                  onNavigate?.();
                }
              })
            )}
          <div className="flex items-center justify-between rounded-md px-3 py-2">
            <span className="text-sm font-medium text-muted-foreground">Tema</span>
            <ThemeToggle />
          </div>
          <button
            className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              logout();
              if (isMobile) {
                onNavigate?.();
              }
            }}
          >
            <LogOut className="h-4 w-4" />
            Oturumu Kapat
          </button>
        </nav>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          "fixed left-4 top-4 z-40 hidden shadow-md",
          isDesktopSidebarVisible ? "md:hidden" : "md:flex"
        )}
      >
        <Button
          variant="outline"
          size="icon"
          className="hidden md:inline-flex"
          onClick={() => setDesktopSidebarVisible(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      <Sheet open={isMobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="fixed left-4 top-4 z-40 shadow-md md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="sidebar-red w-72 border-r bg-card p-0 text-foreground"
        >
          <SidebarContent
            isMobile
            onNavigate={() => setMobileSidebarOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <aside
        className={cn(
          "sidebar-red fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r bg-card text-foreground shadow-xl md:flex lg:w-80",
          !isDesktopSidebarVisible && "md:hidden"
        )}
      >
        <SidebarContent
          onCollapseDesktop={() => setDesktopSidebarVisible(false)}
        />
      </aside>

    </>
  );
}
